// Location type definition
export type Location = {
  city: string;
  country: string;
  countryCode: string; // ISO 3166-1 alpha-2 for flag emoji
  timezone: string;
};

/**
 * Mapping of IANA timezone identifiers to user-friendly display names
 */
export const TIMEZONE_DISPLAY_NAMES: Record<string, string> = {
  // US Cities - Eastern Time
  'America/New_York': 'Eastern Time - New York',

  // US Cities - Central Time
  'America/Chicago': 'Central Time - Chicago',

  // US Cities - Mountain Time
  'America/Denver': 'Mountain Time - Denver',
  'America/Phoenix': 'Mountain Time - Phoenix',

  // US Cities - Pacific Time
  'America/Los_Angeles': 'Pacific Time - Los Angeles',

  // US Cities - Alaska Time
  'America/Anchorage': 'Alaska Time - Anchorage',

  // Canada
  'America/Toronto': 'Eastern Time - Toronto',
  'America/Vancouver': 'Pacific Time - Vancouver',
  'America/Edmonton': 'Mountain Time - Edmonton',
  'America/Winnipeg': 'Central Time - Winnipeg',
  'America/Halifax': 'Atlantic Time - Halifax',

  // Mexico & Central America
  'America/Mexico_City': 'Central Time - Mexico City',
  'America/Monterrey': 'Central Time - Monterrey',
  'America/Tijuana': 'Pacific Time - Tijuana',
  'America/Cancun': 'Eastern Time - Cancun',
  'America/Guatemala': 'Central Time - Guatemala City',
  'America/Costa_Rica': 'Central Time - San Jose',
  'America/Panama': 'Eastern Time - Panama City',

  // South America
  'America/Sao_Paulo': 'Brazil Time - Sao Paulo',
  'America/Argentina/Buenos_Aires': 'Argentina Time - Buenos Aires',
  'America/Argentina/Cordoba': 'Argentina Time - Cordoba',
  'America/Bahia': 'Brazil Time - Salvador',
  'America/Fortaleza': 'Brazil Time - Fortaleza',
  'America/Recife': 'Brazil Time - Recife',
  'America/Manaus': 'Brazil Time - Manaus',
  'America/Lima': 'Peru Time - Lima',
  'America/Bogota': 'Colombia Time - Bogota',
  'America/Caracas': 'Venezuela Time - Caracas',
  'America/Guayaquil': 'Ecuador Time - Guayaquil',
  'America/La_Paz': 'Bolivia Time - La Paz',
  'America/Asuncion': 'Paraguay Time - Asuncion',
  'America/Montevideo': 'Uruguay Time - Montevideo',
  'America/Santiago': 'Chile Time - Santiago',

  // Europe
  'Europe/London': 'Greenwich Mean Time - London',
  'Europe/Dublin': 'Greenwich Mean Time - Dublin',
  'Europe/Lisbon': 'Western European Time - Lisbon',
  'Europe/Paris': 'Central European Time - Paris',
  'Europe/Berlin': 'Central European Time - Berlin',
  'Europe/Rome': 'Central European Time - Rome',
  'Europe/Madrid': 'Central European Time - Madrid',
  'Europe/Amsterdam': 'Central European Time - Amsterdam',
  'Europe/Brussels': 'Central European Time - Brussels',
  'Europe/Vienna': 'Central European Time - Vienna',
  'Europe/Zurich': 'Central European Time - Zurich',
  'Europe/Prague': 'Central European Time - Prague',
  'Europe/Warsaw': 'Central European Time - Warsaw',
  'Europe/Stockholm': 'Central European Time - Stockholm',
  'Europe/Copenhagen': 'Central European Time - Copenhagen',
  'Europe/Oslo': 'Central European Time - Oslo',
  'Europe/Helsinki': 'Eastern European Time - Helsinki',
  'Europe/Athens': 'Eastern European Time - Athens',
  'Europe/Bucharest': 'Eastern European Time - Bucharest',
  'Europe/Sofia': 'Eastern European Time - Sofia',
  'Europe/Budapest': 'Central European Time - Budapest',
  'Europe/Belgrade': 'Central European Time - Belgrade',
  'Europe/Zagreb': 'Central European Time - Zagreb',
  'Europe/Riga': 'Eastern European Time - Riga',
  'Europe/Tallinn': 'Eastern European Time - Tallinn',
  'Europe/Vilnius': 'Eastern European Time - Vilnius',
  'Europe/Moscow': 'Moscow Time',
  'Europe/Kiev': 'Eastern European Time - Kiev',
  'Europe/Istanbul': 'Turkey Time - Istanbul',

  // Middle East
  'Asia/Dubai': 'Gulf Standard Time - Dubai',
  'Asia/Riyadh': 'Arabia Standard Time - Riyadh',
  'Asia/Kuwait': 'Arabia Standard Time - Kuwait',
  'Asia/Qatar': 'Arabia Standard Time - Doha',
  'Asia/Bahrain': 'Arabia Standard Time - Manama',
  'Asia/Muscat': 'Gulf Standard Time - Muscat',
  'Asia/Tehran': 'Iran Time - Tehran',
  'Asia/Baghdad': 'Arabia Standard Time - Baghdad',
  'Asia/Damascus': 'Eastern European Time - Damascus',
  'Asia/Beirut': 'Eastern European Time - Beirut',
  'Asia/Amman': 'Eastern European Time - Amman',
  'Asia/Jerusalem': 'Israel Time - Jerusalem',
  'Asia/Yerevan': 'Armenia Time - Yerevan',
  'Asia/Baku': 'Azerbaijan Time - Baku',
  'Asia/Tbilisi': 'Georgia Time - Tbilisi',

  // Asia
  'Asia/Karachi': 'Pakistan Time - Karachi',
  'Asia/Kolkata': 'India Time - Kolkata',
  'Asia/Dhaka': 'Bangladesh Time - Dhaka',
  'Asia/Colombo': 'Sri Lanka Time - Colombo',
  'Asia/Kathmandu': 'Nepal Time - Kathmandu',
  'Asia/Yangon': 'Myanmar Time - Yangon',
  'Asia/Bangkok': 'Indochina Time - Bangkok',
  'Asia/Ho_Chi_Minh': 'Indochina Time - Ho Chi Minh',
  'Asia/Phnom_Penh': 'Indochina Time - Phnom Penh',
  'Asia/Vientiane': 'Indochina Time - Vientiane',
  'Asia/Kuala_Lumpur': 'Malaysia Time - Kuala Lumpur',
  'Asia/Singapore': 'Singapore Time',
  'Asia/Jakarta': 'Western Indonesia Time - Jakarta',
  'Asia/Makassar': 'Central Indonesia Time - Makassar',
  'Asia/Manila': 'Philippines Time - Manila',
  'Asia/Shanghai': 'China Time - Shanghai',
  'Asia/Hong_Kong': 'Hong Kong Time',
  'Asia/Taipei': 'Taiwan Time - Taipei',
  'Asia/Seoul': 'Korea Time - Seoul',
  'Asia/Tokyo': 'Japan Time - Tokyo',
  'Asia/Ulaanbaatar': 'Mongolia Time - Ulaanbaatar',
  'Asia/Almaty': 'Kazakhstan Time - Almaty',
  'Asia/Tashkent': 'Uzbekistan Time - Tashkent',
  'Asia/Bishkek': 'Kyrgyzstan Time - Bishkek',

  // Africa
  'Africa/Abidjan': 'Greenwich Mean Time - Abidjan',
  'Africa/Accra': 'Greenwich Mean Time - Accra',
  'Africa/Cairo': 'Eastern European Time - Cairo',
  'Africa/Casablanca': 'Western European Time - Casablanca',
  'Africa/Johannesburg': 'South Africa Time - Johannesburg',
  'Africa/Lagos': 'West Africa Time - Lagos',
  'Africa/Nairobi': 'East Africa Time - Nairobi',
  'Africa/Addis_Ababa': 'East Africa Time - Addis Ababa',
  'Africa/Algiers': 'Central European Time - Algiers',
  'Africa/Dakar': 'Greenwich Mean Time - Dakar',
  'Africa/Dar_es_Salaam': 'East Africa Time - Dar es Salaam',
  'Africa/Harare': 'Central Africa Time - Harare',
  'Africa/Kampala': 'East Africa Time - Kampala',
  'Africa/Khartoum': 'Central Africa Time - Khartoum',
  'Africa/Kinshasa': 'West Africa Time - Kinshasa',
  'Africa/Luanda': 'West Africa Time - Luanda',
  'Africa/Lubumbashi': 'Central Africa Time - Lubumbashi',
  'Africa/Maputo': 'Central Africa Time - Maputo',
  'Africa/Tunis': 'Central European Time - Tunis',

  // Australia & Oceania
  'Australia/Sydney': 'Australian Eastern Time - Sydney',
  'Australia/Melbourne': 'Australian Eastern Time - Melbourne',
  'Australia/Brisbane': 'Australian Eastern Time - Brisbane',
  'Australia/Adelaide': 'Australian Central Time - Adelaide',
  'Australia/Darwin': 'Australian Central Time - Darwin',
  'Australia/Perth': 'Australian Western Time - Perth',
  'Australia/Hobart': 'Australian Eastern Time - Hobart',
  'Pacific/Auckland': 'New Zealand Time - Auckland',

  // Pacific
  'Pacific/Honolulu': 'Hawaii Time - Honolulu',
  'Pacific/Fiji': 'Fiji Time',
  'Pacific/Port_Moresby': 'Papua New Guinea Time - Port Moresby',
};

/**
 * Comprehensive list of all available timezones with location information
 */
// prettier-ignore
export const ALL_TIMEZONES: Location[] = [
  // US Cities - Eastern Time
  { city: 'New York', country: 'US', countryCode: 'US', timezone: 'America/New_York' },
  { city: 'Boston', country: 'US', countryCode: 'US', timezone: 'America/New_York' },
  { city: 'Miami', country: 'US', countryCode: 'US', timezone: 'America/New_York' },
  { city: 'Atlanta', country: 'US', countryCode: 'US', timezone: 'America/New_York' },
  { city: 'Washington DC', country: 'US', countryCode: 'US', timezone: 'America/New_York' },
  { city: 'Philadelphia', country: 'US', countryCode: 'US', timezone: 'America/New_York' },
  { city: 'Detroit', country: 'US', countryCode: 'US', timezone: 'America/New_York' },
  { city: 'Baltimore', country: 'US', countryCode: 'US', timezone: 'America/New_York' },
  { city: 'Charlotte', country: 'US', countryCode: 'US', timezone: 'America/New_York' },
  { city: 'Tampa', country: 'US', countryCode: 'US', timezone: 'America/New_York' },
  { city: 'Orlando', country: 'US', countryCode: 'US', timezone: 'America/New_York' },
  { city: 'Jacksonville', country: 'US', countryCode: 'US', timezone: 'America/New_York' },
  { city: 'Pittsburgh', country: 'US', countryCode: 'US', timezone: 'America/New_York' },
  { city: 'Cleveland', country: 'US', countryCode: 'US', timezone: 'America/New_York' },
  { city: 'Columbus', country: 'US', countryCode: 'US', timezone: 'America/New_York' },
  { city: 'Indianapolis', country: 'US', countryCode: 'US', timezone: 'America/New_York' },
  { city: 'Raleigh', country: 'US', countryCode: 'US', timezone: 'America/New_York' },
  { city: 'Richmond', country: 'US', countryCode: 'US', timezone: 'America/New_York' },
  { city: 'Buffalo', country: 'US', countryCode: 'US', timezone: 'America/New_York' },

  // US Cities - Central Time
  { city: 'Chicago', country: 'US', countryCode: 'US', timezone: 'America/Chicago' },
  { city: 'Dallas', country: 'US', countryCode: 'US', timezone: 'America/Chicago' },
  { city: 'Houston', country: 'US', countryCode: 'US', timezone: 'America/Chicago' },
  { city: 'Minneapolis', country: 'US', countryCode: 'US', timezone: 'America/Chicago' },
  { city: 'New Orleans', country: 'US', countryCode: 'US', timezone: 'America/Chicago' },
  { city: 'Nashville', country: 'US', countryCode: 'US', timezone: 'America/Chicago' },
  { city: 'Memphis', country: 'US', countryCode: 'US', timezone: 'America/Chicago' },
  { city: 'Kansas City', country: 'US', countryCode: 'US', timezone: 'America/Chicago' },
  { city: 'San Antonio', country: 'US', countryCode: 'US', timezone: 'America/Chicago' },
  { city: 'Austin', country: 'US', countryCode: 'US', timezone: 'America/Chicago' },
  { city: 'Milwaukee', country: 'US', countryCode: 'US', timezone: 'America/Chicago' },
  { city: 'St. Louis', country: 'US', countryCode: 'US', timezone: 'America/Chicago' },
  { city: 'Oklahoma City', country: 'US', countryCode: 'US', timezone: 'America/Chicago' },
  { city: 'Tulsa', country: 'US', countryCode: 'US', timezone: 'America/Chicago' },
  { city: 'Fort Worth', country: 'US', countryCode: 'US', timezone: 'America/Chicago' },
  { city: 'El Paso', country: 'US', countryCode: 'US', timezone: 'America/Chicago' },

  // US Cities - Mountain Time
  { city: 'Denver', country: 'US', countryCode: 'US', timezone: 'America/Denver' },
  { city: 'Salt Lake City', country: 'US', countryCode: 'US', timezone: 'America/Denver' },
  { city: 'Albuquerque', country: 'US', countryCode: 'US', timezone: 'America/Denver' },
  { city: 'Boise', country: 'US', countryCode: 'US', timezone: 'America/Denver' },
  { city: 'Colorado Springs', country: 'US', countryCode: 'US', timezone: 'America/Denver' },
  { city: 'Phoenix', country: 'US', countryCode: 'US', timezone: 'America/Phoenix' },
  { city: 'Tucson', country: 'US', countryCode: 'US', timezone: 'America/Phoenix' },

  // US Cities - Pacific Time
  { city: 'Los Angeles', country: 'US', countryCode: 'US', timezone: 'America/Los_Angeles' },
  { city: 'San Francisco', country: 'US', countryCode: 'US', timezone: 'America/Los_Angeles' },
  { city: 'Seattle', country: 'US', countryCode: 'US', timezone: 'America/Los_Angeles' },
  { city: 'Portland', country: 'US', countryCode: 'US', timezone: 'America/Los_Angeles' },
  { city: 'San Diego', country: 'US', countryCode: 'US', timezone: 'America/Los_Angeles' },
  { city: 'Las Vegas', country: 'US', countryCode: 'US', timezone: 'America/Los_Angeles' },
  { city: 'San Jose', country: 'US', countryCode: 'US', timezone: 'America/Los_Angeles' },
  { city: 'Oakland', country: 'US', countryCode: 'US', timezone: 'America/Los_Angeles' },
  { city: 'Sacramento', country: 'US', countryCode: 'US', timezone: 'America/Los_Angeles' },
  { city: 'Fresno', country: 'US', countryCode: 'US', timezone: 'America/Los_Angeles' },
  { city: 'Long Beach', country: 'US', countryCode: 'US', timezone: 'America/Los_Angeles' },
  { city: 'Anaheim', country: 'US', countryCode: 'US', timezone: 'America/Los_Angeles' },
  { city: 'Riverside', country: 'US', countryCode: 'US', timezone: 'America/Los_Angeles' },
  { city: 'Santa Ana', country: 'US', countryCode: 'US', timezone: 'America/Los_Angeles' },

  // US Cities - Alaska Time
  { city: 'Anchorage', country: 'US', countryCode: 'US', timezone: 'America/Anchorage' },

  // Canada
  { city: 'Toronto', country: 'Canada', countryCode: 'CA', timezone: 'America/Toronto' },
  { city: 'Montreal', country: 'Canada', countryCode: 'CA', timezone: 'America/Toronto' },
  { city: 'Ottawa', country: 'Canada', countryCode: 'CA', timezone: 'America/Toronto' },
  { city: 'Vancouver', country: 'Canada', countryCode: 'CA', timezone: 'America/Vancouver' },
  { city: 'Victoria', country: 'Canada', countryCode: 'CA', timezone: 'America/Vancouver' },
  { city: 'Edmonton', country: 'Canada', countryCode: 'CA', timezone: 'America/Edmonton' },
  { city: 'Calgary', country: 'Canada', countryCode: 'CA', timezone: 'America/Edmonton' },
  { city: 'Winnipeg', country: 'Canada', countryCode: 'CA', timezone: 'America/Winnipeg' },
  { city: 'Halifax', country: 'Canada', countryCode: 'CA', timezone: 'America/Halifax' },

  // Mexico & Central America
  { city: 'Mexico City', country: 'Mexico', countryCode: 'MX', timezone: 'America/Mexico_City' },
  { city: 'Guadalajara', country: 'Mexico', countryCode: 'MX', timezone: 'America/Mexico_City' },
  { city: 'Puebla', country: 'Mexico', countryCode: 'MX', timezone: 'America/Mexico_City' },
  { city: 'Monterrey', country: 'Mexico', countryCode: 'MX', timezone: 'America/Monterrey' },
  { city: 'Tijuana', country: 'Mexico', countryCode: 'MX', timezone: 'America/Tijuana' },
  { city: 'Cancún', country: 'Mexico', countryCode: 'MX', timezone: 'America/Cancun' },
  { city: 'Guatemala City', country: 'Guatemala', countryCode: 'GT', timezone: 'America/Guatemala' },
  { city: 'San José', country: 'Costa Rica', countryCode: 'CR', timezone: 'America/Costa_Rica' },
  { city: 'Panama City', country: 'Panama', countryCode: 'PA', timezone: 'America/Panama' },

  // South America
  { city: 'São Paulo', country: 'Brazil', countryCode: 'BR', timezone: 'America/Sao_Paulo' },
  { city: 'Rio de Janeiro', country: 'Brazil', countryCode: 'BR', timezone: 'America/Sao_Paulo' },
  { city: 'Brasília', country: 'Brazil', countryCode: 'BR', timezone: 'America/Sao_Paulo' },
  { city: 'Belo Horizonte', country: 'Brazil', countryCode: 'BR', timezone: 'America/Sao_Paulo' },
  { city: 'Curitiba', country: 'Brazil', countryCode: 'BR', timezone: 'America/Sao_Paulo' },
  { city: 'Porto Alegre', country: 'Brazil', countryCode: 'BR', timezone: 'America/Sao_Paulo' },
  { city: 'Salvador', country: 'Brazil', countryCode: 'BR', timezone: 'America/Bahia' },
  { city: 'Fortaleza', country: 'Brazil', countryCode: 'BR', timezone: 'America/Fortaleza' },
  { city: 'Recife', country: 'Brazil', countryCode: 'BR', timezone: 'America/Recife' },
  { city: 'Manaus', country: 'Brazil', countryCode: 'BR', timezone: 'America/Manaus' },
  { city: 'Buenos Aires', country: 'Argentina', countryCode: 'AR', timezone: 'America/Argentina/Buenos_Aires' },
  { city: 'Rosario', country: 'Argentina', countryCode: 'AR', timezone: 'America/Argentina/Buenos_Aires' },
  { city: 'Córdoba', country: 'Argentina', countryCode: 'AR', timezone: 'America/Argentina/Cordoba' },
  { city: 'Lima', country: 'Peru', countryCode: 'PE', timezone: 'America/Lima' },
  { city: 'Bogotá', country: 'Colombia', countryCode: 'CO', timezone: 'America/Bogota' },
  { city: 'Medellín', country: 'Colombia', countryCode: 'CO', timezone: 'America/Bogota' },
  { city: 'Cali', country: 'Colombia', countryCode: 'CO', timezone: 'America/Bogota' },
  { city: 'Barranquilla', country: 'Colombia', countryCode: 'CO', timezone: 'America/Bogota' },
  { city: 'Cartagena', country: 'Colombia', countryCode: 'CO', timezone: 'America/Bogota' },
  { city: 'Santiago', country: 'Chile', countryCode: 'CL', timezone: 'America/Santiago' },
  { city: 'Valparaíso', country: 'Chile', countryCode: 'CL', timezone: 'America/Santiago' },
  { city: 'Caracas', country: 'Venezuela', countryCode: 'VE', timezone: 'America/Caracas' },
  { city: 'Guayaquil', country: 'Ecuador', countryCode: 'EC', timezone: 'America/Guayaquil' },
  { city: 'Quito', country: 'Ecuador', countryCode: 'EC', timezone: 'America/Guayaquil' },
  { city: 'Montevideo', country: 'Uruguay', countryCode: 'UY', timezone: 'America/Montevideo' },
  { city: 'La Paz', country: 'Bolivia', countryCode: 'BO', timezone: 'America/La_Paz' },
  { city: 'Santa Cruz', country: 'Bolivia', countryCode: 'BO', timezone: 'America/La_Paz' },
  { city: 'Asunción', country: 'Paraguay', countryCode: 'PY', timezone: 'America/Asuncion' },

  // Europe
  { city: 'London', country: 'UK', countryCode: 'GB', timezone: 'Europe/London' },
  { city: 'Manchester', country: 'UK', countryCode: 'GB', timezone: 'Europe/London' },
  { city: 'Birmingham', country: 'UK', countryCode: 'GB', timezone: 'Europe/London' },
  { city: 'Glasgow', country: 'UK', countryCode: 'GB', timezone: 'Europe/London' },
  { city: 'Edinburgh', country: 'UK', countryCode: 'GB', timezone: 'Europe/London' },
  { city: 'Liverpool', country: 'UK', countryCode: 'GB', timezone: 'Europe/London' },
  { city: 'Leeds', country: 'UK', countryCode: 'GB', timezone: 'Europe/London' },
  { city: 'Bristol', country: 'UK', countryCode: 'GB', timezone: 'Europe/London' },
  { city: 'Paris', country: 'France', countryCode: 'FR', timezone: 'Europe/Paris' },
  { city: 'Lyon', country: 'France', countryCode: 'FR', timezone: 'Europe/Paris' },
  { city: 'Marseille', country: 'France', countryCode: 'FR', timezone: 'Europe/Paris' },
  { city: 'Nice', country: 'France', countryCode: 'FR', timezone: 'Europe/Paris' },
  { city: 'Toulouse', country: 'France', countryCode: 'FR', timezone: 'Europe/Paris' },
  { city: 'Bordeaux', country: 'France', countryCode: 'FR', timezone: 'Europe/Paris' },
  { city: 'Lille', country: 'France', countryCode: 'FR', timezone: 'Europe/Paris' },
  { city: 'Berlin', country: 'Germany', countryCode: 'DE', timezone: 'Europe/Berlin' },
  { city: 'Munich', country: 'Germany', countryCode: 'DE', timezone: 'Europe/Berlin' },
  { city: 'Frankfurt', country: 'Germany', countryCode: 'DE', timezone: 'Europe/Berlin' },
  { city: 'Hamburg', country: 'Germany', countryCode: 'DE', timezone: 'Europe/Berlin' },
  { city: 'Cologne', country: 'Germany', countryCode: 'DE', timezone: 'Europe/Berlin' },
  { city: 'Stuttgart', country: 'Germany', countryCode: 'DE', timezone: 'Europe/Berlin' },
  { city: 'Düsseldorf', country: 'Germany', countryCode: 'DE', timezone: 'Europe/Berlin' },
  { city: 'Dortmund', country: 'Germany', countryCode: 'DE', timezone: 'Europe/Berlin' },
  { city: 'Essen', country: 'Germany', countryCode: 'DE', timezone: 'Europe/Berlin' },
  { city: 'Leipzig', country: 'Germany', countryCode: 'DE', timezone: 'Europe/Berlin' },
  { city: 'Rome', country: 'Italy', countryCode: 'IT', timezone: 'Europe/Rome' },
  { city: 'Milan', country: 'Italy', countryCode: 'IT', timezone: 'Europe/Rome' },
  { city: 'Naples', country: 'Italy', countryCode: 'IT', timezone: 'Europe/Rome' },
  { city: 'Turin', country: 'Italy', countryCode: 'IT', timezone: 'Europe/Rome' },
  { city: 'Florence', country: 'Italy', countryCode: 'IT', timezone: 'Europe/Rome' },
  { city: 'Venice', country: 'Italy', countryCode: 'IT', timezone: 'Europe/Rome' },
  { city: 'Palermo', country: 'Italy', countryCode: 'IT', timezone: 'Europe/Rome' },
  { city: 'Bologna', country: 'Italy', countryCode: 'IT', timezone: 'Europe/Rome' },
  { city: 'Genoa', country: 'Italy', countryCode: 'IT', timezone: 'Europe/Rome' },
  { city: 'Amsterdam', country: 'Netherlands', countryCode: 'NL', timezone: 'Europe/Amsterdam' },
  { city: 'Rotterdam', country: 'Netherlands', countryCode: 'NL', timezone: 'Europe/Amsterdam' },
  { city: 'Madrid', country: 'Spain', countryCode: 'ES', timezone: 'Europe/Madrid' },
  { city: 'Barcelona', country: 'Spain', countryCode: 'ES', timezone: 'Europe/Madrid' },
  { city: 'Valencia', country: 'Spain', countryCode: 'ES', timezone: 'Europe/Madrid' },
  { city: 'Seville', country: 'Spain', countryCode: 'ES', timezone: 'Europe/Madrid' },
  { city: 'Bilbao', country: 'Spain', countryCode: 'ES', timezone: 'Europe/Madrid' },
  { city: 'Málaga', country: 'Spain', countryCode: 'ES', timezone: 'Europe/Madrid' },
  { city: 'Zaragoza', country: 'Spain', countryCode: 'ES', timezone: 'Europe/Madrid' },
  { city: 'Stockholm', country: 'Sweden', countryCode: 'SE', timezone: 'Europe/Stockholm' },
  { city: 'Gothenburg', country: 'Sweden', countryCode: 'SE', timezone: 'Europe/Stockholm' },
  { city: 'Malmö', country: 'Sweden', countryCode: 'SE', timezone: 'Europe/Stockholm' },
  { city: 'Zurich', country: 'Switzerland', countryCode: 'CH', timezone: 'Europe/Zurich' },
  { city: 'Geneva', country: 'Switzerland', countryCode: 'CH', timezone: 'Europe/Zurich' },
  { city: 'Basel', country: 'Switzerland', countryCode: 'CH', timezone: 'Europe/Zurich' },
  { city: 'Vienna', country: 'Austria', countryCode: 'AT', timezone: 'Europe/Vienna' },
  { city: 'Graz', country: 'Austria', countryCode: 'AT', timezone: 'Europe/Vienna' },
  { city: 'Prague', country: 'Czech Republic', countryCode: 'CZ', timezone: 'Europe/Prague' },
  { city: 'Warsaw', country: 'Poland', countryCode: 'PL', timezone: 'Europe/Warsaw' },
  { city: 'Krakow', country: 'Poland', countryCode: 'PL', timezone: 'Europe/Warsaw' },
  { city: 'Gdansk', country: 'Poland', countryCode: 'PL', timezone: 'Europe/Warsaw' },
  { city: 'Wroclaw', country: 'Poland', countryCode: 'PL', timezone: 'Europe/Warsaw' },
  { city: 'Poznan', country: 'Poland', countryCode: 'PL', timezone: 'Europe/Warsaw' },
  { city: 'Athens', country: 'Greece', countryCode: 'GR', timezone: 'Europe/Athens' },
  { city: 'Thessaloniki', country: 'Greece', countryCode: 'GR', timezone: 'Europe/Athens' },
  { city: 'Istanbul', country: 'Turkey', countryCode: 'TR', timezone: 'Europe/Istanbul' },
  { city: 'Moscow', country: 'Russia', countryCode: 'RU', timezone: 'Europe/Moscow' },
  { city: 'St. Petersburg', country: 'Russia', countryCode: 'RU', timezone: 'Europe/Moscow' },
  { city: 'Lisbon', country: 'Portugal', countryCode: 'PT', timezone: 'Europe/Lisbon' },
  { city: 'Porto', country: 'Portugal', countryCode: 'PT', timezone: 'Europe/Lisbon' },
  { city: 'Dublin', country: 'Ireland', countryCode: 'IE', timezone: 'Europe/Dublin' },
  { city: 'Cork', country: 'Ireland', countryCode: 'IE', timezone: 'Europe/Dublin' },
  { city: 'Brussels', country: 'Belgium', countryCode: 'BE', timezone: 'Europe/Brussels' },
  { city: 'Copenhagen', country: 'Denmark', countryCode: 'DK', timezone: 'Europe/Copenhagen' },
  { city: 'Oslo', country: 'Norway', countryCode: 'NO', timezone: 'Europe/Oslo' },
  { city: 'Helsinki', country: 'Finland', countryCode: 'FI', timezone: 'Europe/Helsinki' },
  { city: 'Bucharest', country: 'Romania', countryCode: 'RO', timezone: 'Europe/Bucharest' },
  { city: 'Cluj-Napoca', country: 'Romania', countryCode: 'RO', timezone: 'Europe/Bucharest' },
  { city: 'Timisoara', country: 'Romania', countryCode: 'RO', timezone: 'Europe/Bucharest' },
  { city: 'Budapest', country: 'Hungary', countryCode: 'HU', timezone: 'Europe/Budapest' },
  { city: 'Zagreb', country: 'Croatia', countryCode: 'HR', timezone: 'Europe/Zagreb' },
  { city: 'Belgrade', country: 'Serbia', countryCode: 'RS', timezone: 'Europe/Belgrade' },
  { city: 'Sofia', country: 'Bulgaria', countryCode: 'BG', timezone: 'Europe/Sofia' },
  { city: 'Vilnius', country: 'Lithuania', countryCode: 'LT', timezone: 'Europe/Vilnius' },
  { city: 'Riga', country: 'Latvia', countryCode: 'LV', timezone: 'Europe/Riga' },
  { city: 'Tallinn', country: 'Estonia', countryCode: 'EE', timezone: 'Europe/Tallinn' },
  { city: 'Kiev', country: 'Ukraine', countryCode: 'UA', timezone: 'Europe/Kiev' },
  { city: 'Kyiv', country: 'Ukraine', countryCode: 'UA', timezone: 'Europe/Kiev' },

  // Middle East
  { city: 'Dubai', country: 'UAE', countryCode: 'AE', timezone: 'Asia/Dubai' },
  { city: 'Abu Dhabi', country: 'UAE', countryCode: 'AE', timezone: 'Asia/Dubai' },
  { city: 'Riyadh', country: 'Saudi Arabia', countryCode: 'SA', timezone: 'Asia/Riyadh' },
  { city: 'Jeddah', country: 'Saudi Arabia', countryCode: 'SA', timezone: 'Asia/Riyadh' },
  { city: 'Doha', country: 'Qatar', countryCode: 'QA', timezone: 'Asia/Qatar' },
  { city: 'Kuwait City', country: 'Kuwait', countryCode: 'KW', timezone: 'Asia/Kuwait' },
  { city: 'Jerusalem', country: 'Israel', countryCode: 'IL', timezone: 'Asia/Jerusalem' },
  { city: 'Tel Aviv', country: 'Israel', countryCode: 'IL', timezone: 'Asia/Jerusalem' },
  { city: 'Beirut', country: 'Lebanon', countryCode: 'LB', timezone: 'Asia/Beirut' },
  { city: 'Amman', country: 'Jordan', countryCode: 'JO', timezone: 'Asia/Amman' },
  { city: 'Muscat', country: 'Oman', countryCode: 'OM', timezone: 'Asia/Muscat' },
  { city: 'Manama', country: 'Bahrain', countryCode: 'BH', timezone: 'Asia/Bahrain' },
  { city: 'Tehran', country: 'Iran', countryCode: 'IR', timezone: 'Asia/Tehran' },
  { city: 'Baghdad', country: 'Iraq', countryCode: 'IQ', timezone: 'Asia/Baghdad' },
  { city: 'Damascus', country: 'Syria', countryCode: 'SY', timezone: 'Asia/Damascus' },
  { city: 'Yerevan', country: 'Armenia', countryCode: 'AM', timezone: 'Asia/Yerevan' },
  { city: 'Baku', country: 'Azerbaijan', countryCode: 'AZ', timezone: 'Asia/Baku' },
  { city: 'Tbilisi', country: 'Georgia', countryCode: 'GE', timezone: 'Asia/Tbilisi' },

  // Asia
  { city: 'Tokyo', country: 'Japan', countryCode: 'JP', timezone: 'Asia/Tokyo' },
  { city: 'Osaka', country: 'Japan', countryCode: 'JP', timezone: 'Asia/Tokyo' },
  { city: 'Kyoto', country: 'Japan', countryCode: 'JP', timezone: 'Asia/Tokyo' },
  { city: 'Yokohama', country: 'Japan', countryCode: 'JP', timezone: 'Asia/Tokyo' },
  { city: 'Nagoya', country: 'Japan', countryCode: 'JP', timezone: 'Asia/Tokyo' },
  { city: 'Fukuoka', country: 'Japan', countryCode: 'JP', timezone: 'Asia/Tokyo' },
  { city: 'Sapporo', country: 'Japan', countryCode: 'JP', timezone: 'Asia/Tokyo' },
  { city: 'Hiroshima', country: 'Japan', countryCode: 'JP', timezone: 'Asia/Tokyo' },
  { city: 'Sendai', country: 'Japan', countryCode: 'JP', timezone: 'Asia/Tokyo' },
  { city: 'Kobe', country: 'Japan', countryCode: 'JP', timezone: 'Asia/Tokyo' },
  { city: 'Shanghai', country: 'China', countryCode: 'CN', timezone: 'Asia/Shanghai' },
  { city: 'Beijing', country: 'China', countryCode: 'CN', timezone: 'Asia/Shanghai' },
  { city: 'Guangzhou', country: 'China', countryCode: 'CN', timezone: 'Asia/Shanghai' },
  { city: 'Shenzhen', country: 'China', countryCode: 'CN', timezone: 'Asia/Shanghai' },
  { city: 'Chengdu', country: 'China', countryCode: 'CN', timezone: 'Asia/Shanghai' },
  { city: 'Xi\'an', country: 'China', countryCode: 'CN', timezone: 'Asia/Shanghai' },
  { city: 'Hangzhou', country: 'China', countryCode: 'CN', timezone: 'Asia/Shanghai' },
  { city: 'Nanjing', country: 'China', countryCode: 'CN', timezone: 'Asia/Shanghai' },
  { city: 'Shenyang', country: 'China', countryCode: 'CN', timezone: 'Asia/Shanghai' },
  { city: 'Tianjin', country: 'China', countryCode: 'CN', timezone: 'Asia/Shanghai' },
  { city: 'Chongqing', country: 'China', countryCode: 'CN', timezone: 'Asia/Shanghai' },
  { city: 'Wuhan', country: 'China', countryCode: 'CN', timezone: 'Asia/Shanghai' },
  { city: 'Suzhou', country: 'China', countryCode: 'CN', timezone: 'Asia/Shanghai' },
  { city: 'Dongguan', country: 'China', countryCode: 'CN', timezone: 'Asia/Shanghai' },
  { city: 'Foshan', country: 'China', countryCode: 'CN', timezone: 'Asia/Shanghai' },
  { city: 'Qingdao', country: 'China', countryCode: 'CN', timezone: 'Asia/Shanghai' },
  { city: 'Dalian', country: 'China', countryCode: 'CN', timezone: 'Asia/Shanghai' },
  { city: 'Singapore', country: 'Singapore', countryCode: 'SG', timezone: 'Asia/Singapore' },
  { city: 'Hong Kong', country: 'Hong Kong', countryCode: 'HK', timezone: 'Asia/Hong_Kong' },
  { city: 'Seoul', country: 'South Korea', countryCode: 'KR', timezone: 'Asia/Seoul' },
  { city: 'Busan', country: 'South Korea', countryCode: 'KR', timezone: 'Asia/Seoul' },
  { city: 'Incheon', country: 'South Korea', countryCode: 'KR', timezone: 'Asia/Seoul' },
  { city: 'Daegu', country: 'South Korea', countryCode: 'KR', timezone: 'Asia/Seoul' },
  { city: 'Gwangju', country: 'South Korea', countryCode: 'KR', timezone: 'Asia/Seoul' },
  { city: 'Ulsan', country: 'South Korea', countryCode: 'KR', timezone: 'Asia/Seoul' },
  { city: 'Bangkok', country: 'Thailand', countryCode: 'TH', timezone: 'Asia/Bangkok' },
  { city: 'Phuket', country: 'Thailand', countryCode: 'TH', timezone: 'Asia/Bangkok' },
  { city: 'Chiang Mai', country: 'Thailand', countryCode: 'TH', timezone: 'Asia/Bangkok' },
  { city: 'Jakarta', country: 'Indonesia', countryCode: 'ID', timezone: 'Asia/Jakarta' },
  { city: 'Surabaya', country: 'Indonesia', countryCode: 'ID', timezone: 'Asia/Jakarta' },
  { city: 'Bandung', country: 'Indonesia', countryCode: 'ID', timezone: 'Asia/Jakarta' },
  { city: 'Medan', country: 'Indonesia', countryCode: 'ID', timezone: 'Asia/Jakarta' },
  { city: 'Makassar', country: 'Indonesia', countryCode: 'ID', timezone: 'Asia/Makassar' },
  { city: 'Denpasar', country: 'Indonesia', countryCode: 'ID', timezone: 'Asia/Makassar' },
  { city: 'Manila', country: 'Philippines', countryCode: 'PH', timezone: 'Asia/Manila' },
  { city: 'Cebu', country: 'Philippines', countryCode: 'PH', timezone: 'Asia/Manila' },
  { city: 'Davao', country: 'Philippines', countryCode: 'PH', timezone: 'Asia/Manila' },
  { city: 'Kuala Lumpur', country: 'Malaysia', countryCode: 'MY', timezone: 'Asia/Kuala_Lumpur' },
  { city: 'Penang', country: 'Malaysia', countryCode: 'MY', timezone: 'Asia/Kuala_Lumpur' },
  { city: 'Johor Bahru', country: 'Malaysia', countryCode: 'MY', timezone: 'Asia/Kuala_Lumpur' },
  { city: 'Ho Chi Minh City', country: 'Vietnam', countryCode: 'VN', timezone: 'Asia/Ho_Chi_Minh' },
  { city: 'Hanoi', country: 'Vietnam', countryCode: 'VN', timezone: 'Asia/Ho_Chi_Minh' },
  { city: 'Kolkata', country: 'India', countryCode: 'IN', timezone: 'Asia/Kolkata' },
  { city: 'Bombay', country: 'India', countryCode: 'IN', timezone: 'Asia/Kolkata' },
  { city: 'Mumbai', country: 'India', countryCode: 'IN', timezone: 'Asia/Kolkata' },
  { city: 'Delhi', country: 'India', countryCode: 'IN', timezone: 'Asia/Kolkata' },
  { city: 'Bangalore', country: 'India', countryCode: 'IN', timezone: 'Asia/Kolkata' },
  { city: 'Chennai', country: 'India', countryCode: 'IN', timezone: 'Asia/Kolkata' },
  { city: 'Hyderabad', country: 'India', countryCode: 'IN', timezone: 'Asia/Kolkata' },
  { city: 'Ahmedabad', country: 'India', countryCode: 'IN', timezone: 'Asia/Kolkata' },
  { city: 'Pune', country: 'India', countryCode: 'IN', timezone: 'Asia/Kolkata' },
  { city: 'Jaipur', country: 'India', countryCode: 'IN', timezone: 'Asia/Kolkata' },
  { city: 'Surat', country: 'India', countryCode: 'IN', timezone: 'Asia/Kolkata' },
  { city: 'Taipei', country: 'Taiwan', countryCode: 'TW', timezone: 'Asia/Taipei' },
  { city: 'Dhaka', country: 'Bangladesh', countryCode: 'BD', timezone: 'Asia/Dhaka' },
  { city: 'Islamabad', country: 'Pakistan', countryCode: 'PK', timezone: 'Asia/Karachi' },
  { city: 'Karachi', country: 'Pakistan', countryCode: 'PK', timezone: 'Asia/Karachi' },
  { city: 'Lahore', country: 'Pakistan', countryCode: 'PK', timezone: 'Asia/Karachi' },
  { city: 'Faisalabad', country: 'Pakistan', countryCode: 'PK', timezone: 'Asia/Karachi' },
  { city: 'Yangon', country: 'Myanmar', countryCode: 'MM', timezone: 'Asia/Yangon' },
  { city: 'Phnom Penh', country: 'Cambodia', countryCode: 'KH', timezone: 'Asia/Phnom_Penh' },
  { city: 'Vientiane', country: 'Laos', countryCode: 'LA', timezone: 'Asia/Vientiane' },
  { city: 'Ulaanbaatar', country: 'Mongolia', countryCode: 'MN', timezone: 'Asia/Ulaanbaatar' },
  { city: 'Kathmandu', country: 'Nepal', countryCode: 'NP', timezone: 'Asia/Kathmandu' },
  { city: 'Colombo', country: 'Sri Lanka', countryCode: 'LK', timezone: 'Asia/Colombo' },
  { city: 'Tashkent', country: 'Uzbekistan', countryCode: 'UZ', timezone: 'Asia/Tashkent' },
  { city: 'Almaty', country: 'Kazakhstan', countryCode: 'KZ', timezone: 'Asia/Almaty' },
  { city: 'Astana', country: 'Kazakhstan', countryCode: 'KZ', timezone: 'Asia/Almaty' },
  { city: 'Bishkek', country: 'Kyrgyzstan', countryCode: 'KG', timezone: 'Asia/Bishkek' },

  // Africa
  { city: 'Cairo', country: 'Egypt', countryCode: 'EG', timezone: 'Africa/Cairo' },
  { city: 'Johannesburg', country: 'South Africa', countryCode: 'ZA', timezone: 'Africa/Johannesburg' },
  { city: 'Cape Town', country: 'South Africa', countryCode: 'ZA', timezone: 'Africa/Johannesburg' },
  { city: 'Lagos', country: 'Nigeria', countryCode: 'NG', timezone: 'Africa/Lagos' },
  { city: 'Nairobi', country: 'Kenya', countryCode: 'KE', timezone: 'Africa/Nairobi' },
  { city: 'Casablanca', country: 'Morocco', countryCode: 'MA', timezone: 'Africa/Casablanca' },
  { city: 'Rabat', country: 'Morocco', countryCode: 'MA', timezone: 'Africa/Casablanca' },
  { city: 'Tunis', country: 'Tunisia', countryCode: 'TN', timezone: 'Africa/Tunis' },
  { city: 'Accra', country: 'Ghana', countryCode: 'GH', timezone: 'Africa/Accra' },
  { city: 'Dar es Salaam', country: 'Tanzania', countryCode: 'TZ', timezone: 'Africa/Dar_es_Salaam' },
  { city: 'Addis Ababa', country: 'Ethiopia', countryCode: 'ET', timezone: 'Africa/Addis_Ababa' },
  { city: 'Algiers', country: 'Algeria', countryCode: 'DZ', timezone: 'Africa/Algiers' },
  { city: 'Kinshasa', country: 'DRC', countryCode: 'CD', timezone: 'Africa/Kinshasa' },
  { city: 'Lubumbashi', country: 'DRC', countryCode: 'CD', timezone: 'Africa/Lubumbashi' },
  { city: 'Abidjan', country: 'Ivory Coast', countryCode: 'CI', timezone: 'Africa/Abidjan' },
  { city: 'Dakar', country: 'Senegal', countryCode: 'SN', timezone: 'Africa/Dakar' },
  { city: 'Kampala', country: 'Uganda', countryCode: 'UG', timezone: 'Africa/Kampala' },
  { city: 'Khartoum', country: 'Sudan', countryCode: 'SD', timezone: 'Africa/Khartoum' },
  { city: 'Luanda', country: 'Angola', countryCode: 'AO', timezone: 'Africa/Luanda' },
  { city: 'Maputo', country: 'Mozambique', countryCode: 'MZ', timezone: 'Africa/Maputo' },
  { city: 'Harare', country: 'Zimbabwe', countryCode: 'ZW', timezone: 'Africa/Harare' },

  // Australia & Oceania
  { city: 'Sydney', country: 'Australia', countryCode: 'AU', timezone: 'Australia/Sydney' },
  { city: 'Canberra', country: 'Australia', countryCode: 'AU', timezone: 'Australia/Sydney' },
  { city: 'Newcastle', country: 'Australia', countryCode: 'AU', timezone: 'Australia/Sydney' },
  { city: 'Wollongong', country: 'Australia', countryCode: 'AU', timezone: 'Australia/Sydney' },
  { city: 'Melbourne', country: 'Australia', countryCode: 'AU', timezone: 'Australia/Melbourne' },
  { city: 'Geelong', country: 'Australia', countryCode: 'AU', timezone: 'Australia/Melbourne' },
  { city: 'Brisbane', country: 'Australia', countryCode: 'AU', timezone: 'Australia/Brisbane' },
  { city: 'Gold Coast', country: 'Australia', countryCode: 'AU', timezone: 'Australia/Brisbane' },
  { city: 'Perth', country: 'Australia', countryCode: 'AU', timezone: 'Australia/Perth' },
  { city: 'Adelaide', country: 'Australia', countryCode: 'AU', timezone: 'Australia/Adelaide' },
  { city: 'Auckland', country: 'New Zealand', countryCode: 'NZ', timezone: 'Pacific/Auckland' },
  { city: 'Wellington', country: 'New Zealand', countryCode: 'NZ', timezone: 'Pacific/Auckland' },
  { city: 'Christchurch', country: 'New Zealand', countryCode: 'NZ', timezone: 'Pacific/Auckland' },
  { city: 'Hamilton', country: 'New Zealand', countryCode: 'NZ', timezone: 'Pacific/Auckland' },
  { city: 'Darwin', country: 'Australia', countryCode: 'AU', timezone: 'Australia/Darwin' },
  { city: 'Hobart', country: 'Australia', countryCode: 'AU', timezone: 'Australia/Hobart' },

  // Pacific
  { city: 'Honolulu', country: 'US', countryCode: 'US', timezone: 'Pacific/Honolulu' },
  { city: 'Fiji', country: 'Fiji', countryCode: 'FJ', timezone: 'Pacific/Fiji' },
  { city: 'Port Moresby', country: 'Papua New Guinea', countryCode: 'PG', timezone: 'Pacific/Port_Moresby' },

  // Fallback
  { city: 'UTC', country: 'UTC', countryCode: 'UN', timezone: 'UTC' },
];
